import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

/**
 * Lightweight replacement for @ng-idle/core + @ng-idle/keepalive (no Angular-major-locked dependency).
 */
@Injectable({ providedIn: 'root' })
export class IdleService implements OnDestroy {
  onIdleStart = new Subject<void>();
  onIdleEnd = new Subject<void>();
  onTimeoutWarning = new Subject<number>();
  onTimeout = new Subject<void>();

  private idleMs = 0;
  private timeoutMs = 0;
  private interruptEvents = ['keydown', 'mousedown', 'touchstart', 'touchmove', 'wheel', 'scroll'];

  private destroy$ = new Subject<void>();
  private watchSub?: Subscription;
  private countdownHandle?: ReturnType<typeof setInterval>;
  private idling = false;

  constructor(private ngZone: NgZone) {}

  setIdle(seconds: number): void {
    this.idleMs = seconds * 1000;
  }

  setTimeout(seconds: number): void {
    this.timeoutMs = seconds * 1000;
  }

  setInterrupts(events: string[]): void {
    this.interruptEvents = events;
  }

  watch(): void {
    this.stopInternal();
    this.idling = false;

    const activity$ = merge(...this.interruptEvents.map(evt => fromEvent(document, evt)));

    this.ngZone.runOutsideAngular(() => {
      this.watchSub = activity$.pipe(
        debounceTime(300),
        switchMap(() => timer(this.idleMs)),
        takeUntil(this.destroy$)
      ).subscribe(() => this.ngZone.run(() => this.beginIdle()));
    });
  }

  stop(): void {
    this.stopInternal();
  }

  private beginIdle(): void {
    this.idling = true;
    this.onIdleStart.next();
    this.startCountdown();
  }

  private startCountdown(): void {
    let remaining = Math.floor(this.timeoutMs / 1000);
    this.onTimeoutWarning.next(remaining);
    this.countdownHandle = setInterval(() => {
      remaining--;
      this.onTimeoutWarning.next(remaining);
      if (remaining <= 0) {
        this.clearCountdown();
        this.onTimeout.next();
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
      this.countdownHandle = undefined;
    }
  }

  private stopInternal(): void {
    this.clearCountdown();
    this.watchSub?.unsubscribe();
    if (this.idling) {
      this.idling = false;
      this.onIdleEnd.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopInternal();
  }
}
